import React, { useEffect, useRef, useState } from "react";
import { fetchFromStrapi } from "../api/strapi";
import { SkeletonBlock, EmptyState } from "./SkeletonUI";

/* ─── Grid card ──────────────────────────────────────────────────── */
function GridCard({ product, onLearnMore }) {
  return (
    <div
      className="ip-card"
      style={{ "--product-color": product.color }}
      onClick={onLearnMore}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onLearnMore()}
    >
      <div className="ip-card__accent" />
      <div className="ip-card__body">
        <div className="ip-card__logo">
          {product.logo && (
            <img src={product.logo} alt={product.name} loading="lazy" />
          )}
        </div>
        <h3 className="ip-card__name">{product.name}</h3>
        {product.description && (
          <p className="ip-card__desc">{product.description}</p>
        )}
        <button className="ip-card__cta">
          Learn more 
          {/* <i className="bi bi-arrow-right ms-1" /> */}
        </button>
      </div>
    </div>
  );
}

/* ─── List row ───────────────────────────────────────────────────── */
function ListRow({ product, onLearnMore }) {
  return (
    <div
      className="ip-list-row"
      style={{ "--product-color": product.color }}
      onClick={onLearnMore}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onLearnMore()}
    >
      <div className="ip-list-row__logo">
        {product.logo && (
          <img src={product.logo} alt={product.name} loading="lazy" />
        )}
      </div>
      <div className="ip-list-row__body">
        <p className="ip-list-row__name">{product.name}</p>
        {product.description && (
          <p className="ip-list-row__desc">{product.description}</p>
        )}
      </div>
      <button className="ip-list-row__cta" tabIndex={-1}>
        Learn more 
      </button>
    </div>
  );
}

/* ─── Loading skeleton ───────────────────────────────────────────── */
function GridSkeleton() {
  return (
    <div className="ip-skeleton-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="ip-skeleton-card">
          <SkeletonBlock width={72} height={72} radius={8} />
          <SkeletonBlock height={18} style={{ marginTop: 6 }} />
          <SkeletonBlock width="80%" height={14} />
          <SkeletonBlock height={14} />
          <SkeletonBlock width="60%" height={14} />
          <SkeletonBlock width={90} height={32} radius={20} style={{ marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
}

/* ─── Detail modal ───────────────────────────────────────────────── */
function IpModal({ ipDetails, loading, onClose }) {
  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const attrs = ipDetails?.attributes;

  return (
    <div className="ip-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ip-modal" onClick={e => e.stopPropagation()}>
        <div style={{ height: 4, flexShrink: 0 }} /> {/* top accent bar background: "linear-gradient(90deg, #009EDC, #00ddff)",*/}

        <div className="ip-modal__body">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
            <button className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          {loading && (
            <div>
              <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 24 }}>
                <SkeletonBlock width={80} height={80} radius={8} />
                <div style={{ flex: 1 }}>
                  <SkeletonBlock height={28} style={{ marginBottom: 10 }} />
                  <SkeletonBlock width="55%" height={18} />
                </div>
              </div>
              <SkeletonBlock height={14} style={{ marginBottom: 8 }} />
              <SkeletonBlock height={14} style={{ marginBottom: 8 }} />
              <SkeletonBlock width="70%" height={14} />
            </div>
          )}

          {!loading && attrs && (
            <>
              <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 24 }}>
                {attrs.logo?.data?.[0] && (
                  <img
                    src={attrs.logo.data[0].attributes.url}
                    alt={attrs.title}
                    style={{ width: 100, height: 100, objectFit: "contain", flexShrink: 0 }}
                  />
                )}
                <h2 className="fw-bold mb-0" style={{ color: "#0a1a3a", fontSize: "clamp(1.25rem, 2vw, 1.6rem)" }}>
                  {attrs.title}
                </h2>
              </div>

              <hr style={{ borderColor: "#f0f0f0", margin: "0 0 20px" }} />

              <p style={{ color: "#4B5563", lineHeight: 1.8, marginBottom: 24 }}>
                {attrs.description}
              </p>

              {(attrs.attachment?.data?.attributes?.url || attrs.website) && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 28 }}>
                  {attrs.attachment?.data?.attributes?.url && (
                    <a
                      href={attrs.attachment.data.attributes.url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-dark"
                    >
                      <i className="bi bi-download me-1" />
                      Download data sheet
                    </a>
                  )}
                  {attrs.website && (
                    <a
                      href={attrs.website}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-dark"
                    >
                      Visit website
                      <i className="bi bi-arrow-up-right ms-1" />
                    </a>
                  )}
                </div>
              )}

              {attrs.gallery?.data?.length > 0 && (
                <>
                  <p className="fw-semibold" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.07em", color: "#6b7280", marginBottom: 12 }}>
                    Gallery
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                    {attrs.gallery.data.map(img => (
                      <div key={img.id} style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
                        <img
                          src={img.attributes.url}
                          alt={img.attributes.alternativeText || "Product image"}
                          style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
const Products = () => {
  const [products, setProducts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [view, setView]                 = useState("grid");
  const [modalOpen, setModalOpen]       = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [ipDetails, setIpDetails]       = useState(null);
  const sectionRef                      = useRef(null);

  useEffect(() => {
    fetchFromStrapi("/ecl-Ips")
      .then(res => {
        const list = res?.data?.data || res?.data || [];
        setProducts(
          list.map((ip, i) => ({
            id:          ip.id || i + 1,
            name:        ip.attributes?.title  || "",
            logo:        ip.attributes?.logo?.data?.[0]?.attributes?.url || "",
            description: ip.attributes?.description || "",
            color:       ip.attributes?.color  || "#009EDC",
          }))
        );
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  /* Scroll reveal — fires once when section enters viewport */
  useEffect(() => {
    if (loading) return;
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.querySelectorAll(".scroll-fade-up").forEach(el =>
            el.classList.add("is-visible")
          );
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [loading, products, view]);

  const openModal = async id => {
    setModalOpen(true);
    setModalLoading(true);
    setIpDetails(null);
    try {
      const res = await fetchFromStrapi(`/ecl-Ips/${id}`);
      setIpDetails(res?.data?.data || res?.data);
    } catch {
      // modal stays open; user can close
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setIpDetails(null);
  };

  return (
    <section className="ip-section" ref={sectionRef}>
      <div className="container">

        <div className="ip-section-header scroll-fade-up" style={{ "--reveal-delay": "0s" }}>
          <div>
            <p
              className="text-uppercase fw-semibold mb-1"
              style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}
            >
              Innovation in Action
            </p>
            <h2
              className="fw-bold mb-0"
              style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", color: "#0a1a3a" }}
            >
              Our IPs
            </h2>
          </div>

          {/* {!loading && products.length > 0 && (
            <div className="ip-layout-toggle" role="group" aria-label="Switch layout">
              <button
                className={view === "grid" ? "active" : ""}
                onClick={() => setView("grid")}
                title="Grid view"
                aria-pressed={view === "grid"}
              >
                <i className="bi bi-grid-3x3" />
              </button>
              <button
                className={view === "list" ? "active" : ""}
                onClick={() => setView("list")}
                title="List view"
                aria-pressed={view === "list"}
              >
                <i className="bi bi-list-ul" />
              </button>
            </div>
          )} */}
        </div>

        {loading ? (
          <GridSkeleton />
        ) : products.length === 0 ? (
          <EmptyState
            title="No products yet"
            description="Our in-house products will be listed here soon."
          />
        ) : view === "grid" ? (
          <div className="ip-grid">
            {products.map((p, i) => (
              <div key={p.id} className="scroll-fade-up" style={{ "--reveal-delay": `${0.1 + i * 0.06}s` }}>
                <GridCard product={p} onLearnMore={() => openModal(p.id)} />
              </div>
            ))}
          </div>
        ) : (
          <div className="ip-list">
            {products.map((p, i) => (
              <div key={p.id} className="scroll-fade-up" style={{ "--reveal-delay": `${0.1 + i * 0.06}s` }}>
                <ListRow product={p} onLearnMore={() => openModal(p.id)} />
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <IpModal ipDetails={ipDetails} loading={modalLoading} onClose={closeModal} />
      )}
    </section>
  );
};

export default Products;
